import { ApiError, api } from '@invisoutlet/shared/api';
import type { AddOnKey, Address, Building, Device, Entity, User } from '@invisoutlet/shared/types';
import { buildingsLabel, formatDate } from '@invisoutlet/shared/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { InternalLayout } from '../components/internal-layout.js';
import {
  ADD_ON_DESCRIPTIONS,
  ADD_ON_KEYS,
  ADD_ON_LABELS,
  NOTIFICATION_MODE_OPTIONS,
  TEMPERATURE_UNIT_OPTIONS,
} from '../constants/entity.js';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AdminUserListItem extends User {
  entityName?: string;
}

// ─── Tab type ─────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'buildings' | 'devices' | 'users' | 'monitoring';

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'buildings', label: 'Buildings' },
  { id: 'devices', label: 'Devices' },
  { id: 'users', label: 'Users' },
  { id: 'monitoring', label: 'Monitoring' },
];

// ─── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab({ entity }: { entity: Entity }) {
  const queryClient = useQueryClient();
  const [savingAddon, setSavingAddon] = useState<AddOnKey | null>(null);

  const addonMutation = useMutation({
    mutationFn: ({ key, enabled }: { key: AddOnKey; enabled: boolean }) =>
      api.patch(`/api/admin/entities/${entity.id}/addons`, { [key]: enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-entity', entity.id] });
      setSavingAddon(null);
    },
    onError: () => {
      setSavingAddon(null);
    },
  });

  function toggleAddOn(key: AddOnKey) {
    setSavingAddon(key);
    addonMutation.mutate({ key, enabled: !entity.addOns[key] });
  }

  return (
    <div className="space-y-8">
      {/* Entity info */}
      <div className="rounded-xl border border-border bg-bg-surface p-6">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-text-secondary">
          Entity Details
        </h3>
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-xs text-text-secondary">Name</dt>
            <dd className="mt-0.5 text-sm font-medium text-text-primary">{entity.name}</dd>
          </div>
          <div>
            <dt className="text-xs text-text-secondary">Entity ID</dt>
            <dd className="mt-0.5 font-mono text-xs text-text-secondary">{entity.id}</dd>
          </div>
          <div>
            <dt className="text-xs text-text-secondary">Created</dt>
            <dd className="mt-0.5 text-sm text-text-primary">{formatDate(entity.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-xs text-text-secondary">Temperature Unit</dt>
            <dd className="mt-0.5 text-sm text-text-primary">
              {TEMPERATURE_UNIT_OPTIONS.find((o) => o.value === entity.preference.temperatureUnit)
                ?.label ?? entity.preference.temperatureUnit}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-text-secondary">Notification Mode</dt>
            <dd className="mt-0.5 text-sm text-text-primary">
              {NOTIFICATION_MODE_OPTIONS.find((o) => o.value === entity.preference.notification)
                ?.label ?? entity.preference.notification}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-text-secondary">Buildings</dt>
            <dd className="mt-0.5 text-sm text-text-primary">{entity.buildings.length}</dd>
          </div>
        </dl>
      </div>

      {/* Add-on toggles */}
      <div className="rounded-xl border border-border bg-bg-surface p-6">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-text-secondary">
          Add-ons
        </h3>
        <div className="space-y-3">
          {ADD_ON_KEYS.map((key) => {
            const enabled = entity.addOns[key] ?? false;
            const isSaving = savingAddon === key && addonMutation.isPending;
            return (
              <div
                key={key}
                className="flex items-center justify-between rounded-lg border border-border bg-bg-primary p-4"
              >
                <div>
                  <p className="text-sm font-medium text-text-primary">{ADD_ON_LABELS[key]}</p>
                  <p className="text-xs text-text-secondary">{ADD_ON_DESCRIPTIONS[key]}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  aria-label={`Toggle ${ADD_ON_LABELS[key]}`}
                  disabled={isSaving}
                  onClick={() => toggleAddOn(key)}
                  className={[
                    'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:opacity-50',
                    enabled ? 'bg-accent' : 'bg-bg-elevated',
                  ].join(' ')}
                >
                  <span
                    aria-hidden="true"
                    className={[
                      'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ease-in-out',
                      enabled ? 'translate-x-4' : 'translate-x-0',
                    ].join(' ')}
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Level enum editor ────────────────────────────────────────────────────────

interface LevelEntry {
  key: string;
  value: string;
}

function levelEnumToEntries(levelEnum: Record<string, string>): LevelEntry[] {
  return Object.entries(levelEnum).map(([key, value]) => ({ key, value }));
}

function entriesToLevelEnum(entries: LevelEntry[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const { key, value } of entries) {
    if (key.trim()) result[key.trim()] = value;
  }
  return result;
}

interface LevelEnumEditorProps {
  entries: LevelEntry[];
  onChange: (entries: LevelEntry[]) => void;
}

function LevelEnumEditor({ entries, onChange }: LevelEnumEditorProps) {
  function addEntry() {
    onChange([...entries, { key: '', value: '' }]);
  }

  function removeEntry(index: number) {
    onChange(entries.filter((_, i) => i !== index));
  }

  function updateEntry(index: number, field: 'key' | 'value', val: string) {
    onChange(entries.map((e, i) => (i === index ? { ...e, [field]: val } : e)));
  }

  return (
    <div className="space-y-2">
      {entries.map((entry, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: stable list during session
        <div key={i} className="flex items-center gap-2">
          <input
            type="text"
            value={entry.key}
            onChange={(e) => updateEntry(i, 'key', e.target.value)}
            placeholder="Key (e.g. 1, M)"
            aria-label={`Level key ${i + 1}`}
            className="w-28 rounded-lg border border-border bg-bg-surface px-3 py-2 font-mono text-sm text-text-primary outline-none placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
          <input
            type="text"
            value={entry.value}
            onChange={(e) => updateEntry(i, 'value', e.target.value)}
            placeholder="Label (e.g. Floor 1)"
            aria-label={`Level label ${i + 1}`}
            className="flex-1 rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
          <button
            type="button"
            onClick={() => removeEntry(i)}
            aria-label="Remove level"
            className="rounded-lg p-1.5 text-danger hover:bg-danger/10 focus:outline-none focus:ring-2 focus:ring-danger"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addEntry}
        className="flex items-center gap-1.5 rounded-md px-1 py-0.5 text-sm text-accent hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-accent"
      >
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        Add Level
      </button>
    </div>
  );
}

// ─── Buildings tab ────────────────────────────────────────────────────────────

interface AddBuildingModalProps {
  entityId: string;
  onClose: () => void;
  onCreated: () => void;
}

function AddBuildingModal({ entityId, onClose, onCreated }: AddBuildingModalProps) {
  const [name, setName] = useState('');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [addrState, setAddrState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('US');
  const [levelEntries, setLevelEntries] = useState<LevelEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: { name: string; address?: Address; levelEnum?: Record<string, string> }) =>
      api.post<Building>(`/api/admin/entities/${entityId}/buildings`, body),
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to add building.');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Building name is required.');
      return;
    }
    mutation.mutate({
      name: name.trim(),
      address: line1.trim()
        ? {
            line1: line1.trim(),
            line2: line2.trim() || undefined,
            city: city.trim(),
            state: addrState.trim(),
            postalCode: postalCode.trim(),
            country: country.trim() || 'US',
          }
        : undefined,
      levelEnum: levelEntries.length > 0 ? entriesToLevelEnum(levelEntries) : undefined,
    });
  }

  return (
    <BuildingFormModal
      titleId="add-building-title"
      title="Add Building"
      submitLabel="Add Building"
      pendingLabel="Adding…"
      isPending={mutation.isPending}
      error={error}
      onClose={onClose}
      onSubmit={handleSubmit}
      name={name}
      onNameChange={setName}
      line1={line1}
      onLine1Change={setLine1}
      line2={line2}
      onLine2Change={setLine2}
      city={city}
      onCityChange={setCity}
      addrState={addrState}
      onAddrStateChange={setAddrState}
      postalCode={postalCode}
      onPostalCodeChange={setPostalCode}
      country={country}
      onCountryChange={setCountry}
      levelEntries={levelEntries}
      onLevelEntriesChange={setLevelEntries}
    />
  );
}

// ─── Shared building form modal ───────────────────────────────────────────────

interface BuildingFormModalProps {
  titleId: string;
  title: string;
  submitLabel: string;
  pendingLabel: string;
  isPending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  name: string;
  onNameChange: (v: string) => void;
  line1: string;
  onLine1Change: (v: string) => void;
  line2: string;
  onLine2Change: (v: string) => void;
  city: string;
  onCityChange: (v: string) => void;
  addrState: string;
  onAddrStateChange: (v: string) => void;
  postalCode: string;
  onPostalCodeChange: (v: string) => void;
  country: string;
  onCountryChange: (v: string) => void;
  levelEntries: LevelEntry[];
  onLevelEntriesChange: (entries: LevelEntry[]) => void;
}

function BuildingFormModal({
  titleId,
  title,
  submitLabel,
  pendingLabel,
  isPending,
  error,
  onClose,
  onSubmit,
  name,
  onNameChange,
  line1,
  onLine1Change,
  line2,
  onLine2Change,
  city,
  onCityChange,
  addrState,
  onAddrStateChange,
  postalCode,
  onPostalCodeChange,
  country,
  onCountryChange,
  levelEntries,
  onLevelEntriesChange,
}: BuildingFormModalProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-xl border border-border bg-bg-primary shadow-xl">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <h2 id={titleId} className="text-base font-semibold text-text-primary">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-text-secondary hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable form body */}
        <form onSubmit={onSubmit} noValidate className="flex min-h-0 flex-col">
          <div className="overflow-y-auto px-6 py-5">
            <div className="space-y-5">
              {/* Name */}
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor={`${titleId}-name`}
                  className="text-sm font-medium text-text-primary"
                >
                  Name{' '}
                  <span className="text-danger" aria-hidden="true">
                    *
                  </span>
                </label>
                <input
                  id={`${titleId}-name`}
                  type="text"
                  required
                  value={name}
                  onChange={(e) => onNameChange(e.target.value)}
                  placeholder="Vacation Inn Chicago Downtown"
                  className="rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
                />
              </div>

              {/* Address */}
              <div className="space-y-3 rounded-lg border border-border bg-bg-surface p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
                  Address
                </p>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor={`${titleId}-line1`}
                    className="text-sm font-medium text-text-primary"
                  >
                    Street Address
                  </label>
                  <input
                    id={`${titleId}-line1`}
                    type="text"
                    value={line1}
                    onChange={(e) => onLine1Change(e.target.value)}
                    placeholder="123 Main St"
                    className="rounded-lg border border-border bg-bg-primary px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor={`${titleId}-line2`}
                    className="text-sm font-medium text-text-primary"
                  >
                    Suite / Unit
                    <span className="ml-1 font-normal text-text-secondary">(optional)</span>
                  </label>
                  <input
                    id={`${titleId}-line2`}
                    type="text"
                    value={line2}
                    onChange={(e) => onLine2Change(e.target.value)}
                    placeholder="STE 1000"
                    className="rounded-lg border border-border bg-bg-primary px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor={`${titleId}-city`}
                      className="text-sm font-medium text-text-primary"
                    >
                      City
                    </label>
                    <input
                      id={`${titleId}-city`}
                      type="text"
                      value={city}
                      onChange={(e) => onCityChange(e.target.value)}
                      placeholder="Chicago"
                      className="rounded-lg border border-border bg-bg-primary px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor={`${titleId}-state`}
                      className="text-sm font-medium text-text-primary"
                    >
                      State
                    </label>
                    <input
                      id={`${titleId}-state`}
                      type="text"
                      value={addrState}
                      onChange={(e) => onAddrStateChange(e.target.value)}
                      placeholder="IL"
                      maxLength={2}
                      className="rounded-lg border border-border bg-bg-primary px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor={`${titleId}-postal`}
                      className="text-sm font-medium text-text-primary"
                    >
                      Postal Code
                    </label>
                    <input
                      id={`${titleId}-postal`}
                      type="text"
                      value={postalCode}
                      onChange={(e) => onPostalCodeChange(e.target.value)}
                      placeholder="60601"
                      className="rounded-lg border border-border bg-bg-primary px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor={`${titleId}-country`}
                      className="text-sm font-medium text-text-primary"
                    >
                      Country
                    </label>
                    <input
                      id={`${titleId}-country`}
                      type="text"
                      value={country}
                      onChange={(e) => onCountryChange(e.target.value)}
                      placeholder="US"
                      maxLength={2}
                      className="rounded-lg border border-border bg-bg-primary px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
                    />
                  </div>
                </div>
              </div>

              {/* Level enum */}
              <div className="space-y-3 rounded-lg border border-border bg-bg-surface p-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
                    Floor / Level Map
                  </p>
                  <p className="mt-0.5 text-xs text-text-secondary">
                    Define the key used in device assignment (e.g. "M") and its display label (e.g.
                    "Mezzanine").
                  </p>
                </div>
                <LevelEnumEditor entries={levelEntries} onChange={onLevelEntriesChange} />
              </div>
            </div>
          </div>

          {/* Footer — always visible */}
          <div className="shrink-0 border-t border-border px-6 py-4">
            {error && (
              <p
                role="alert"
                className="mb-3 rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger"
              >
                {error}
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-accent"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isPending}
                className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-50"
              >
                {isPending ? pendingLabel : submitLabel}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Edit building modal ─────────────────────────────────────────────────────

interface EditBuildingModalProps {
  entityId: string;
  building: Building;
  onClose: () => void;
  onSaved: () => void;
}

function EditBuildingModal({ entityId, building, onClose, onSaved }: EditBuildingModalProps) {
  const [name, setName] = useState(building.name);
  const [line1, setLine1] = useState(building.address?.line1 ?? '');
  const [line2, setLine2] = useState(building.address?.line2 ?? '');
  const [city, setCity] = useState(building.address?.city ?? '');
  const [addrState, setAddrState] = useState(building.address?.state ?? '');
  const [postalCode, setPostalCode] = useState(building.address?.postalCode ?? '');
  const [country, setCountry] = useState(building.address?.country ?? 'US');
  const [levelEntries, setLevelEntries] = useState<LevelEntry[]>(() =>
    levelEnumToEntries(building.levelEnum ?? {}),
  );
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: { name: string; address?: Address; levelEnum: Record<string, string> }) =>
      api.patch(`/api/admin/entities/${entityId}/buildings/${building.id}`, body),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to update building.');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Building name is required.');
      return;
    }
    mutation.mutate({
      name: name.trim(),
      address: line1.trim()
        ? {
            line1: line1.trim(),
            line2: line2.trim() || undefined,
            city: city.trim(),
            state: addrState.trim(),
            postalCode: postalCode.trim(),
            country: country.trim() || 'US',
          }
        : undefined,
      levelEnum: entriesToLevelEnum(levelEntries),
    });
  }

  return (
    <BuildingFormModal
      titleId="edit-building-title"
      title="Edit Building"
      submitLabel="Save Changes"
      pendingLabel="Saving…"
      isPending={mutation.isPending}
      error={error}
      onClose={onClose}
      onSubmit={handleSubmit}
      name={name}
      onNameChange={setName}
      line1={line1}
      onLine1Change={setLine1}
      line2={line2}
      onLine2Change={setLine2}
      city={city}
      onCityChange={setCity}
      addrState={addrState}
      onAddrStateChange={setAddrState}
      postalCode={postalCode}
      onPostalCodeChange={setPostalCode}
      country={country}
      onCountryChange={setCountry}
      levelEntries={levelEntries}
      onLevelEntriesChange={setLevelEntries}
    />
  );
}

function BuildingsTab({ entity }: { entity: Entity }) {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState<Building | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (buildingId: string) =>
      api.delete(`/api/admin/entities/${entity.id}/buildings/${buildingId}`),
    onSuccess: () => {
      setDeleteError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin-entity', entity.id] });
    },
    onError: (err) => {
      setDeleteError(err instanceof ApiError ? err.message : 'Failed to remove building.');
    },
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin-entity', entity.id] });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          {entity.buildings.length} building{entity.buildings.length !== 1 ? 's' : ''}
        </p>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add Building
        </button>
      </div>

      {deleteError && (
        <p role="alert" className="rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">
          {deleteError}
        </p>
      )}

      {entity.buildings.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-bg-surface py-16 text-center">
          <p className="text-sm font-medium text-text-primary">No buildings yet</p>
          <p className="mt-1 text-xs text-text-secondary">Add a building to get started.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-bg-surface">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Location
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Levels
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-bg-primary">
              {entity.buildings.map((b) => (
                <tr key={b.id} className="hover:bg-bg-surface">
                  <td className="px-4 py-3">
                    <p className="font-medium text-text-primary">{b.name}</p>
                    {b.address?.line1 && (
                      <p className="text-xs text-text-secondary">{b.address.line1}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-text-secondary">
                    {b.address?.city && b.address?.state
                      ? `${b.address.city}, ${b.address.state}${b.address.postalCode ? ` ${b.address.postalCode}` : ''}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-right text-text-secondary">
                    {Object.keys(b.levelEnum ?? {}).length}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingBuilding(b)}
                        className="rounded-md px-2.5 py-1 text-xs font-medium text-accent hover:bg-accent/10 focus:outline-none focus:ring-2 focus:ring-accent"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteMutation.mutate(b.id)}
                        disabled={deleteMutation.isPending}
                        className="rounded-md px-2.5 py-1 text-xs font-medium text-danger hover:bg-danger/10 focus:outline-none focus:ring-2 focus:ring-danger disabled:opacity-40"
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AddBuildingModal
          entityId={entity.id}
          onClose={() => setShowAdd(false)}
          onCreated={invalidate}
        />
      )}

      {editingBuilding && (
        <EditBuildingModal
          entityId={entity.id}
          building={editingBuilding}
          onClose={() => setEditingBuilding(null)}
          onSaved={invalidate}
        />
      )}
    </div>
  );
}

// ─── Devices tab ──────────────────────────────────────────────────────────────

function DevicesTab({ entity }: { entity: Entity }) {
  const queryClient = useQueryClient();
  const [serialNumber, setSerialNumber] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-entity-devices', entity.id],
    queryFn: () => api.get<Device[]>(`/api/admin/entities/${entity.id}/devices`),
  });
  const devices: Device[] = (data?.data as Device[] | undefined) ?? [];

  const addMutation = useMutation({
    mutationFn: (deviceId: string) =>
      api.post<Device>(`/api/admin/entities/${entity.id}/devices`, { deviceId }),
    onSuccess: () => {
      setSerialNumber('');
      setAddError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin-entity-devices', entity.id] });
    },
    onError: (err) => {
      setAddError(err instanceof ApiError ? err.message : 'Failed to add device.');
    },
  });

  function handleAddDevice(e: FormEvent) {
    e.preventDefault();
    setAddError(null);
    if (!serialNumber.trim()) return;
    addMutation.mutate(serialNumber.trim());
  }

  const buildingMap = Object.fromEntries(entity.buildings.map((b) => [b.id, b.name]));

  return (
    <div className="space-y-4">
      {/* Add device form */}
      <form onSubmit={handleAddDevice} className="flex items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="device-sn" className="text-sm font-medium text-text-primary">
            Add Device by Serial Number
          </label>
          <input
            id="device-sn"
            type="text"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            placeholder="IO-XXXX-XXXX"
            className="rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm font-mono text-text-primary outline-none placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
        </div>
        <button
          type="submit"
          disabled={!serialNumber.trim() || addMutation.isPending}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-50"
        >
          {addMutation.isPending ? 'Adding…' : 'Add'}
        </button>
      </form>
      {addError && (
        <p role="alert" className="rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">
          {addError}
        </p>
      )}

      {isLoading ? (
        <div className="py-10 text-center text-sm text-text-secondary">Loading devices…</div>
      ) : devices.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-bg-surface py-16 text-center">
          <p className="text-sm font-medium text-text-primary">No devices provisioned</p>
          <p className="mt-1 text-xs text-text-secondary">
            Add a device by its serial number above.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-bg-surface">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Serial
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Building
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Room
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-bg-primary">
              {devices.map((d) => (
                <tr key={d.deviceId} className="hover:bg-bg-surface">
                  <td className="px-4 py-3 font-mono text-xs text-text-secondary">{d.deviceId}</td>
                  <td className="px-4 py-3 text-text-primary">{d.deviceName || '—'}</td>
                  <td className="px-4 py-3 text-text-secondary">
                    {buildingMap[d.building] ?? d.building ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-text-secondary">{d.room || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Users tab ────────────────────────────────────────────────────────────────

interface AddUserModalProps {
  entity: Entity;
  onClose: () => void;
  onCreated: () => void;
}

function AddUserModal({ entity, onClose, onCreated }: AddUserModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [selfService, setSelfService] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: {
      name: string;
      email: string;
      password: string;
      readOnly: boolean;
      selfService: boolean;
    }) => api.post<User>(`/api/admin/entities/${entity.id}/users`, body),
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create user.');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !email.trim() || !password) {
      setError('Name, email and password are required.');
      return;
    }
    mutation.mutate({ name: name.trim(), email: email.trim(), password, readOnly, selfService });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-user-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-md rounded-xl border border-border bg-bg-primary shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 id="add-user-title" className="text-base font-semibold text-text-primary">
              Add User
            </h2>
            <p className="text-xs text-text-secondary">{entity.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-text-secondary hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate className="space-y-4 p-6">
          <div className="grid grid-cols-1 gap-4">
            {(
              [
                {
                  id: 'add-user-name',
                  label: 'Full Name',
                  value: name,
                  onChange: setName,
                  type: 'text',
                  placeholder: 'Jane Smith',
                },
                {
                  id: 'add-user-email',
                  label: 'Email',
                  value: email,
                  onChange: setEmail,
                  type: 'email',
                  placeholder: 'jane@company.com',
                },
                {
                  id: 'add-user-password',
                  label: 'Temporary Password',
                  value: password,
                  onChange: setPassword,
                  type: 'password',
                  placeholder: '••••••••',
                },
              ] as const
            ).map(({ id, label, value, onChange, type, placeholder }) => (
              <div key={id} className="flex flex-col gap-1.5">
                <label htmlFor={id} className="text-sm font-medium text-text-primary">
                  {label}{' '}
                  <span className="text-danger" aria-hidden="true">
                    *
                  </span>
                </label>
                <input
                  id={id}
                  type={type}
                  required
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder={placeholder}
                  className="rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
                />
              </div>
            ))}
          </div>

          <div className="space-y-2 rounded-lg border border-border bg-bg-surface p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
              Permission Flags
            </p>
            {[
              { label: 'Read Only', value: readOnly, onChange: setReadOnly },
              { label: 'Self Service', value: selfService, onChange: setSelfService },
            ].map(({ label, value, onChange }) => (
              <label key={label} className="flex cursor-pointer items-center justify-between py-1">
                <span className="text-sm text-text-primary">{label}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={value}
                  onClick={() => onChange((v) => !v)}
                  className={[
                    'relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2',
                    value ? 'bg-accent' : 'bg-bg-elevated',
                  ].join(' ')}
                >
                  <span
                    aria-hidden="true"
                    className={[
                      'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200',
                      value ? 'translate-x-4' : 'translate-x-0',
                    ].join(' ')}
                  />
                </button>
              </label>
            ))}
          </div>

          {error && (
            <p role="alert" className="rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-accent"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-50"
            >
              {mutation.isPending ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EntityUsersTab({ entity }: { entity: Entity }) {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [disableError, setDisableError] = useState<string | null>(null);

  const {
    data,
    isLoading,
    error: usersError,
  } = useQuery({
    queryKey: ['admin-entity-users', entity.id],
    queryFn: () => api.get<AdminUserListItem[]>(`/api/admin/users?entityId=${entity.id}&limit=200`),
  });
  const users: AdminUserListItem[] = (data?.data as AdminUserListItem[] | undefined) ?? [];

  const disableMutation = useMutation({
    mutationFn: (uid: string) => api.delete(`/api/admin/users/${uid}`),
    onSuccess: () => {
      setDisableError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin-entity-users', entity.id] });
    },
    onError: (err) => {
      setDisableError(err instanceof ApiError ? err.message : 'Failed to disable user.');
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          {users.length} user{users.length !== 1 ? 's' : ''}
        </p>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add User
        </button>
      </div>

      {disableError && (
        <p role="alert" className="rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">
          {disableError}
        </p>
      )}

      {isLoading ? (
        <div className="py-10 text-center text-sm text-text-secondary">Loading users…</div>
      ) : usersError ? (
        <div role="alert" className="rounded-lg bg-danger/10 px-4 py-3 text-sm text-danger">
          {usersError instanceof ApiError ? usersError.message : 'Failed to load users.'}
        </div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-bg-surface py-16 text-center">
          <p className="text-sm font-medium text-text-primary">No users yet</p>
          <p className="mt-1 text-xs text-text-secondary">Add the first user for this entity.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-bg-surface">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Flags
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Buildings
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Created
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-bg-primary">
              {users.map((u) => (
                <tr key={u.uid} className="hover:bg-bg-surface">
                  <td className="px-4 py-3">
                    <p className="font-medium text-text-primary">{u.name}</p>
                    <p className="text-xs text-text-secondary">{u.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {u.readOnly && (
                        <span className="rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
                          Read Only
                        </span>
                      )}
                      {u.selfService && (
                        <span className="rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
                          Self Service
                        </span>
                      )}
                      {!u.readOnly && !u.selfService && (
                        <span className="text-xs text-text-secondary">Default</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-text-secondary">
                    {buildingsLabel(u.buildings, 'All')}
                  </td>
                  <td className="px-4 py-3 text-text-secondary">{formatDate(u.createdAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => disableMutation.mutate(u.uid)}
                      disabled={disableMutation.isPending}
                      className="rounded-md px-2.5 py-1 text-xs font-medium text-danger hover:bg-danger/10 focus:outline-none focus:ring-2 focus:ring-danger disabled:opacity-40"
                    >
                      Disable
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AddUserModal
          entity={entity}
          onClose={() => setShowAdd(false)}
          onCreated={() =>
            void queryClient.invalidateQueries({ queryKey: ['admin-entity-users', entity.id] })
          }
        />
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function EntityDetailPage() {
  const { entityId } = useParams<{ entityId: string }>();
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-entity', entityId],
    queryFn: () => api.get<Entity>(`/api/admin/entities/${entityId}`),
    enabled: !!entityId,
  });

  const entity = data?.data as Entity | undefined;

  return (
    <InternalLayout title={entity?.name ?? 'Entity Detail'}>
      <div className="flex flex-col gap-6">
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-2 text-sm text-text-secondary"
        >
          <Link to="/entities" className="hover:text-text-primary hover:underline">
            Entities
          </Link>
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
          <span className="text-text-primary">{entity?.name ?? entityId}</span>
        </nav>

        {isLoading ? (
          <div className="flex items-center justify-center py-24 text-sm text-text-secondary">
            Loading entity…
          </div>
        ) : error ? (
          <div role="alert" className="rounded-lg bg-danger/10 px-4 py-3 text-sm text-danger">
            {error instanceof ApiError ? error.message : 'Failed to load entity.'}
          </div>
        ) : !entity ? null : (
          <>
            {/* Entity title */}
            <div>
              <h2 className="text-2xl font-semibold text-text-primary">{entity.name}</h2>
              <p className="mt-0.5 font-mono text-xs text-text-secondary">{entity.id}</p>
            </div>

            {/* Tabs */}
            <div className="border-b border-border">
              <nav className="-mb-px flex gap-1" aria-label="Entity tabs">
                {TABS.map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setActiveTab(id)}
                    aria-current={activeTab === id ? 'page' : undefined}
                    className={[
                      'px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-inset',
                      activeTab === id
                        ? 'border-b-2 border-accent text-accent'
                        : 'text-text-secondary hover:text-text-primary',
                    ].join(' ')}
                  >
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab content */}
            <div>
              {activeTab === 'overview' && <OverviewTab entity={entity} />}
              {activeTab === 'buildings' && <BuildingsTab entity={entity} />}
              {activeTab === 'devices' && <DevicesTab entity={entity} />}
              {activeTab === 'users' && <EntityUsersTab entity={entity} />}
              {activeTab === 'monitoring' && (
                <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-bg-surface py-24 text-center">
                  <p className="text-sm font-medium text-text-primary">Monitoring</p>
                  <p className="mt-1 text-xs text-text-secondary">
                    Live sensor view scoped to this entity — coming soon.
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </InternalLayout>
  );
}
